import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cutlery, Truck, Camera, Bag, NavArrowDown, Calendar, Community } from 'iconoir-react';
import { getLojaInfo, verifyToken } from '../services/api';
import SEOMetaTags from '../components/SEOMetaTags';
import Footer from '../components/Footer';
import LoginModal from '../components/LoginModal';
import MeusPedidosOffcanvas from '../components/MeusPedidosOffcanvas';
import '../components/Loja.css';

const Loja = ({ brand }) => {
  const [loja, setLoja] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isFechamentoTemporario, setIsFechamentoTemporario] = useState(false);
  const [fechamentoData, setFechamentoData] = useState(null);
  const [showHorarios, setShowHorarios] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showMeusPedidos, setShowMeusPedidos] = useState(false);
  const [usuario, setUsuario] = useState(null);

  useEffect(() => {
    fetchLojaData();
    
    // Polling para atualizar estado de fechamento temporário a cada 15 segundos
    const pollInterval = setInterval(() => {
      fetchLojaData();
    }, 15000);
    
    return () => clearInterval(pollInterval);
  }, [brand]);

  useEffect(() => {
    const verificarToken = async () => {
      const token = localStorage.getItem('sitri_token');
      if (token) {
        try {
          const resultado = await verifyToken(token);
          if (resultado && resultado.success && resultado.usuario) {
            setUsuario(resultado.usuario);
          }
        } catch (error) {}
      } else {
        const usuarioSalvo = localStorage.getItem('sitri_user');
        if (usuarioSalvo) {
          try { setUsuario(JSON.parse(usuarioSalvo)); } catch (error) {}
        }
      }
    };
    verificarToken();
  }, []);

  const fetchLojaData = async () => {
    try {
      const response = await getLojaInfo(brand);
      const lojaData = response.loja;
      setLoja(lojaData);
      checkIfOpen(lojaData);

      // Verificar fechamento temporário
      if (lojaData.fechamento_temporario?.ativo) {
        setIsFechamentoTemporario(true);
        setFechamentoData(lojaData.fechamento_temporario);
      } else {
        setIsFechamentoTemporario(false);
        setFechamentoData(null);
      }

      if (lojaData.galeria && Array.isArray(lojaData.galeria) && lojaData.galeria.length > 0) {
        const randomIndex = Math.floor(Math.random() * lojaData.galeria.length);
        setBackgroundImage(`url(https://grubupload.s3.amazonaws.com/${lojaData.galeria[randomIndex].url})`);
      } else if (lojaData.logo_url) {
        setBackgroundImage(`url(https://grubupload.s3.amazonaws.com/${lojaData.logo_url})`);
      } else {
        setBackgroundImage('#000000');
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const checkIfOpen = (lojaData) => {
    if (!lojaData?.horarios || lojaData.horarios.length === 0) return;
    try {
      const today = new Date();
      const diasSemana = [1, 2, 4, 8, 16, 32, 64];
      const diaAtual = diasSemana[today.getDay()];
      const horarioHoje = lojaData.horarios.find(h => h.dia === diaAtual && h.ativo);
      if (!horarioHoje) { setIsOpen(false); return; }
      const [hiHora, hiMin] = horarioHoje.horario_inicial.split(':').map(Number);
      const [hfHora, hfMin] = horarioHoje.horario_final.split(':').map(Number);
      const horaAtual = today.getHours() * 60 + today.getMinutes();
      const horaInicio = hiHora * 60 + hiMin;
      const horaFim = hfHora * 60 + hfMin;
      setIsOpen(horaAtual >= horaInicio && horaAtual <= horaFim);
    } catch (error) {
      setIsOpen(false);
    }
  };

  const handleLogin = (userData) => {
    setUsuario(userData);
    localStorage.setItem('sitri_user', JSON.stringify(userData));
    setShowLogin(false);
  };

  const handleLocationClick = () => {
    if (loja?.endereco) {
      const { logradouro, numero, cidade } = loja.endereco;
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${logradouro}, ${cidade}, ${numero}&travelmode=driving`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="loja-page">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!loja) {
    return (
      <div className="loja-page">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <h6 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a1a' }}>Loja não encontrada</h6>
        </div>
      </div>
    );
  }

  // Links principais
  const mainLinks = [];
  mainLinks.push({ to: '/cardapio', icon: <Cutlery width={24} height={24} />, label: 'Ver Cardápio' });
  if (loja.configuracoes?.b_delivery === 1) mainLinks.push({ to: '/delivery', icon: <Truck width={24} height={24} />, label: 'Pedir Delivery', badge: 'DESCONTOS' });
  if (loja.configuracoes?.b_balcao === 1) mainLinks.push({ to: '/balcao', icon: <Bag width={24} height={24} />, label: 'Pedir no Balcão' });
  mainLinks.push({ to: '/qr-scanner', icon: <Camera width={24} height={24} />, label: 'Ler QR Code' });
  if (loja.configuracoes?.b_fila === 1) mainLinks.push({ to: '/fila', icon: <Community width={24} height={24} />, label: 'Fila de Atendimento' });
  mainLinks.push({ to: '/reserva', icon: <Calendar width={24} height={24} />, label: 'Reservar Mesa' });

  // Helper para formatar data de reabertura
  const formatReabertura = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Verificar se link deve estar desabilitado (quando fechada temporariamente, exceto Ver Cardápio)
  const isLinkDisabled = (link) => {
    // Ver Cardápio sempre fica habilitado - não afeta operações da loja
    if (link.to === '/cardapio') return false;
    return isFechamentoTemporario;
  };

  // Links externos (redes sociais)
  const socialLinks = [];
  if (loja.instagram) socialLinks.push({ href: loja.instagram, icon: <img src="https://grubicons.s3.us-east-1.amazonaws.com/logo-instagram.png" alt="Instagram" width={32} height={32} />, label: 'Instagram' });
  if (loja.twitter) socialLinks.push({ href: loja.twitter, icon: <img src="https://grubicons.s3.us-east-1.amazonaws.com/IFood_logo.svg" alt="iFood" width={24} height={24} />, label: 'iFood' });
  if (loja.whatsapp) socialLinks.push({ href: `https://api.whatsapp.com/send?phone=55${loja.whatsapp}`, icon: <img src="https://grubicons.s3.us-east-1.amazonaws.com/logo-whatsapp.png" alt="WhatsApp" width={32} height={32} />, label: 'WhatsApp' });
  if (loja.endereco) socialLinks.push({ onClick: handleLocationClick, icon: <img src="https://grubicons.s3.us-east-1.amazonaws.com/logo-google-maps.png" alt="Como Chegar" width={32} height={32} />, label: 'Como Chegar' });

  const horariosList = loja.horarios && loja.horarios.length > 0 ? (
    <div className="loja-horarios-lista-content">
      {loja.horarios.map((horario, index) => {
        const dias = { 1: 'Domingo', 2: 'Segunda', 4: 'Terça', 8: 'Quarta', 16: 'Quinta', 32: 'Sexta', 64: 'Sábado' };
        return (
          <div key={index} className="d-flex justify-content-between align-items-center" style={{ fontSize: '0.85rem' }}>
            <span>{dias[horario.dia]}</span>
            <span style={{ fontWeight: 600 }}>
              {horario.ativo ? `${horario.horario_inicial.substring(0, 5)} - ${horario.horario_final.substring(0, 5)}` : 'Fechado'}
            </span>
          </div>
        );
      })}
    </div>
  ) : null;

  return (
    <>
      <SEOMetaTags loja={loja} brand={brand} />
      <div className={`loja-page${isFechamentoTemporario ? ' loja-page--fechada' : ''}`}>

        {/* ===== DESKTOP LAYOUT ===== */}
        <div className="loja-desktop">
          <div className="loja-cover" style={{
            backgroundColor: '#000'
          }}>
            {/* Imagem de fundo - com grayscale se fechada */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: backgroundImage || 'none',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center center',
              backgroundSize: 'cover',
              filter: isFechamentoTemporario ? 'grayscale(1)' : (loja.galeria && loja.galeria.length > 0 ? 'none' : 'blur(8px)'),
              zIndex: 0
            }} />
            <div className="loja-cover-overlay" />
            <div className="loja-cover-content">
              {loja.logo_url && (
                <img
                  src={`https://grubupload.s3.amazonaws.com/${loja.logo_url}`}
                  alt={loja.nome}
                  className="loja-cover-logo"
                />
              )}
              <h1 className="loja-cover-nome">{loja.nome}</h1>
              {loja.tipo && (
                <span className="loja-cover-tipo">{loja.tipo}</span>
              )}
              {loja.horarios && loja.horarios.length > 0 && (
                <div className="loja-cover-status">
                  {isFechamentoTemporario ? (
                    <span className="badge bg-warning text-dark rounded-pill status-badge">TEMPORARIAMENTE FECHADA</span>
                  ) : isOpen ? (
                    <span className="badge bg-success rounded-pill status-badge">ABERTO</span>
                  ) : (
                    <span className="badge bg-danger rounded-pill status-badge">FECHADO</span>
                  )}
                  {isFechamentoTemporario && (fechamentoData?.motivo || fechamentoData?.reabertura_programada) && (
                    <div className="mt-2" style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '8px', padding: '8px 14px', display: 'inline-block' }}>
                      {fechamentoData?.motivo && (
                        <span className="text-white d-block" style={{ fontSize: '0.85rem' }}>
                          {fechamentoData.motivo}
                        </span>
                      )}
                      {fechamentoData?.reabertura_programada && (
                        <span className="text-white d-block" style={{ fontSize: '0.8rem', opacity: 0.85, marginTop: fechamentoData?.motivo ? '4px' : '0' }}>
                          🕐 Reabertura prevista: {formatReabertura(fechamentoData.reabertura_programada)}
                        </span>
                      )}
                    </div>
                  )}
                  <button
                    className="loja-cover-horarios-btn"
                    onClick={() => setShowHorarios(!showHorarios)}
                  >
                    Horários de funcionamento
                    <NavArrowDown
                      width={14}
                      height={14}
                      style={{ transform: showHorarios ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                    />
                  </button>
                </div>
              )}
              {showHorarios && (
                <div className="loja-cover-horarios-lista">
                  {horariosList}
                </div>
              )}
            </div>
          </div>

          <div className="loja-body">
            <div className="loja-section">
              <h5 className="loja-section-title">Como podemos te atender hoje?</h5>
              <div className="loja-links-grid">
                {mainLinks.map((link, i) => (
                  isLinkDisabled(link) ? (
                    <div key={i} className="loja-grid-card loja-grid-card--disabled" title="Loja temporariamente fechada">
                      <div className="loja-grid-icon">{link.icon}</div>
                      <span className="loja-grid-label">{link.label}</span>
                    </div>
                  ) : (
                    <Link key={i} to={link.to} className={`loja-grid-card${link.badge ? ' loja-grid-card--has-badge' : ''}`}>
                      {link.badge && (
                        <div className="loja-ribbon">
                          {link.badge}
                          <div className="loja-ribbon-shine" />
                        </div>
                      )}
                      <div className="loja-grid-icon">{link.icon}</div>
                      <span className="loja-grid-label">{link.label}</span>
                    </Link>
                  )
                ))}
              </div>
            </div>

            {socialLinks.length > 0 && (
              <div className="loja-section">
                <h5 className="loja-section-title">Nossos canais e contatos:</h5>
                <div className="loja-links-grid">
                  {socialLinks.map((link, i) => (
                    link.onClick ? (
                      <button key={i} className="loja-grid-card" onClick={link.onClick}>
                        <div className="loja-grid-icon">{link.icon}</div>
                        <span className="loja-grid-label">{link.label}</span>
                      </button>
                    ) : (
                      <a key={i} href={link.href} target="_blank" rel="noopener noreferrer" className="loja-grid-card">
                        <div className="loja-grid-icon">{link.icon}</div>
                        <span className="loja-grid-label">{link.label}</span>
                      </a>
                    )
                  ))}
                </div>
              </div>
            )}

           
          </div>
        </div>

        {/* ===== MOBILE LAYOUT ===== */}
        <div className="loja-mobile">
          <div className="container py-3">
            <div className="row justify-content-center">
              <div className="col-12">
                <div className="mb-3" style={{ position: 'relative', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                  <div style={{ position: 'relative', height: '120px', overflow: 'hidden' }}>
                    {/* Imagem de fundo - com grayscale se fechada */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: backgroundImage || 'none',
                      backgroundColor: '#000',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center center',
                      backgroundSize: 'cover',
                      filter: isFechamentoTemporario ? 'grayscale(1)' : (loja.galeria && loja.galeria.length > 0 ? 'none' : 'blur(8px)')
                    }} />
                  </div>
                  <div className="p-3 text-center" style={{ marginTop: '-48px', position: 'relative', zIndex: 2 }}>
                    {loja.logo_url && (
                      <img
                        src={`https://grubupload.s3.amazonaws.com/${loja.logo_url}`}
                        alt={loja.nome}
                        className="mb-2"
                        style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '3px solid #fff', position: 'relative', zIndex: 3 }}
                      />
                    )}
                    <h2 style={{ fontWeight: 700, color: '#1a1a1a', fontSize: '1.5rem', marginBottom: '4px' }}>{loja.nome}</h2>
                    {loja.tipo && (
                      <div className="mb-2">
                        <span className="badge text-uppercase text-black" style={{ backgroundColor: '#f4f4f5', letterSpacing: '0.03em', fontSize: '0.7rem', fontWeight: 700 }}>{loja.tipo}</span>
                      </div>
                    )}
                    {loja.endereco && (
                      <p className="mb-2" style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                        {loja.endereco.logradouro} {loja.endereco.numero}
                        {loja.endereco.complemento && ` ${loja.endereco.complemento}`}
                        {' '}{loja.endereco.bairro}
                        {loja.endereco.cidade && ` - ${loja.endereco.cidade}`}
                      </p>
                    )}
                    {loja.horarios && loja.horarios.length > 0 && (
                      <div className="text-center">
                        <div className="mb-2">
                          {isFechamentoTemporario ? (
                            <span className="badge bg-warning text-dark rounded-pill status-badge">TEMPORARIAMENTE FECHADA</span>
                          ) : isOpen ? (
                            <span className="badge bg-success rounded-pill status-badge">ABERTO</span>
                          ) : (
                            <span className="badge bg-danger rounded-pill status-badge">FECHADO</span>
                          )}
                        </div>
                        {isFechamentoTemporario && (fechamentoData?.motivo || fechamentoData?.reabertura_programada) && (
                          <div className="mt-2 mb-2" style={{ background: '#fef3c7', borderRadius: '8px', padding: '8px 14px', display: 'inline-block' }}>
                            {fechamentoData?.motivo && (
                              <span className="d-block" style={{ fontSize: '0.85rem', color: '#92400e' }}>
                                {fechamentoData.motivo}
                              </span>
                            )}
                            {fechamentoData?.reabertura_programada && (
                              <span className="d-block" style={{ fontSize: '0.8rem', color: '#92400e', opacity: 0.85, marginTop: fechamentoData?.motivo ? '4px' : '0' }}>
                                🕐 Reabertura prevista: {formatReabertura(fechamentoData.reabertura_programada)}
                              </span>
                            )}
                          </div>
                        )}
                        <button
                          className="btn btn-link text-decoration-none p-0 d-inline-flex align-items-center"
                          style={{ fontSize: '0.85rem', color: '#6b7280', gap: '4px' }}
                          onClick={() => setShowHorarios(!showHorarios)}
                        >
                          Horários de funcionamento
                          <NavArrowDown
                            width={16}
                            height={16}
                            style={{ transform: showHorarios ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                          />
                        </button>
                        {showHorarios && (
                          <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
                            <div className="d-flex flex-column" style={{ gap: '6px' }}>
                              {loja.horarios.map((horario, index) => {
                                const dias = { 1: 'Domingo', 2: 'Segunda', 4: 'Terça', 8: 'Quarta', 16: 'Quinta', 32: 'Sexta', 64: 'Sábado' };
                                return (
                                  <div key={index} className="d-flex justify-content-between align-items-center" style={{ fontSize: '0.9rem' }}>
                                    <span style={{ color: '#4b5563', fontWeight: 500 }}>{dias[horario.dia]}</span>
                                    <span style={{ color: horario.ativo ? '#1a1a1a' : '#9ca3af', fontWeight: 600 }}>
                                      {horario.ativo ? `${horario.horario_inicial.substring(0, 5)} - ${horario.horario_final.substring(0, 5)}` : 'Fechado'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="loja-mobile-grid">
                  {mainLinks.map((link, i) => (
                    isLinkDisabled(link) ? (
                      <div key={i} className="loja-grid-card loja-grid-card--disabled" title="Loja temporariamente fechada">
                        <div className="loja-grid-icon">{link.icon}</div>
                        <span className="loja-grid-label">{link.label}</span>
                      </div>
                    ) : (
                      <Link key={i} to={link.to} className={`loja-grid-card${link.badge ? ' loja-grid-card--has-badge' : ''}`}>
                        {link.badge && (
                          <div className="loja-ribbon">
                            {link.badge}
                            <div className="loja-ribbon-shine" />
                          </div>
                        )}
                        <div className="loja-grid-icon">{link.icon}</div>
                        <span className="loja-grid-label">{link.label}</span>
                      </Link>
                    )
                  ))}
                  {socialLinks.map((link, i) => (
                    link.onClick ? (
                      <button key={`ext-${i}`} className="loja-grid-card" onClick={link.onClick}>
                        <div className="loja-grid-icon">{link.icon}</div>
                        <span className="loja-grid-label">{link.label}</span>
                      </button>
                    ) : (
                      <a key={`ext-${i}`} href={link.href} target="_blank" rel="noopener noreferrer" className="loja-grid-card">
                        <div className="loja-grid-icon">{link.icon}</div>
                        <span className="loja-grid-label">{link.label}</span>
                      </a>
                    )
                  ))}
                </div>

                <div className="mt-4 text-center">
                  <a href="https://grub.digital" target="_blank" rel="noopener noreferrer">
                    <img src="https://grubicons.s3.us-east-1.amazonaws.com/powered-by-grub.png" alt="Powered by Grub" style={{ maxWidth: '160px', width: '100%' }} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer
          loja={loja}
          onMeusPedidosClick={() => setShowMeusPedidos(true)}
          onLoginClick={() => setShowLogin(true)}
          onCriarContaClick={() => setShowLogin(true)}
        />

        <LoginModal
          show={showLogin}
          onHide={() => setShowLogin(false)}
          onLogin={handleLogin}
          brandLogoUrl={loja?.logo_url}
        />

        {showMeusPedidos && (
          <MeusPedidosOffcanvas
            show={showMeusPedidos}
            onHide={() => setShowMeusPedidos(false)}
          />
        )}

      </div>
    </>
  );
};

export default Loja;
